import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { schedulesApi, venuesApi, usersApi, leaguesApi } from '../services/api';
import './Schedules.css';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
];

function Schedules({ user, onLogout }) {
  const [schedules, setSchedules] = useState([]);
  const [venues, setVenues] = useState([]);
  const [dealers, setDealers] = useState([]);  // all users with dealer role
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    locationId: '',
    gameType: 'Texas Holdem',
    maxPlaces: 10,
    numTables: 1,
    recurrenceType: 'weekly', // 'once', 'daily', 'weekly', 'custom'
    isRecurring: true,
    dayOfWeek: [],
    timeOfDay: '18:30',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    isActive: true,
  });

  // Dealer assignments in the modal: { [tableNumber]: dealerId }
  const [dealerAssignments, setDealerAssignments] = useState({});

  // Generate games progress
  const [generating, setGenerating] = useState(false);
  const [generateProgress, setGenerateProgress] = useState({ step: '', percent: 0, scheduleName: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const leagueIdRef = useRef(user?.league?.id);

  const fetchData = async () => {
    try {
      setLoading(true);

      // If league is missing from user session, fetch it
      if (!leagueIdRef.current) {
        try {
          const leaguesRes = await leaguesApi.getAll();
          const leagues = leaguesRes.data?.leagues || leaguesRes.leagues || [];
          if (leagues.length > 0) {
            leagueIdRef.current = leagues[0].id;
          }
        } catch (err) {
          console.warn('Could not fetch league:', err);
        }
      }

      const leagueId = leagueIdRef.current;
      const [schedulesRes, venuesRes, dealersRes] = await Promise.all([
        schedulesApi.getAll(leagueId),
        venuesApi.getAll(leagueId),
        usersApi.getDealers(),
      ]);

      const schedulesList = schedulesRes.data?.schedules || schedulesRes.schedules || [];
      const venuesList = venuesRes.data?.venues || venuesRes.venues || [];
      const dealersList = dealersRes.data?.dealers || dealersRes.dealers || [];

      setSchedules(Array.isArray(schedulesList) ? schedulesList : []);
      setVenues(Array.isArray(venuesList) ? venuesList : []);
      setDealers(Array.isArray(dealersList) ? dealersList : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (schedule = null) => {
    if (schedule) {
      setEditingSchedule(schedule);
      // Determine recurrence type from existing data
      let recurrenceType = 'once';
      if (schedule.isRecurring) {
        const days = schedule.dayOfWeek || [];
        if (days.length === 7) {
          recurrenceType = 'daily';
        } else if (days.length > 0) {
          recurrenceType = 'custom';
        } else {
          recurrenceType = 'weekly';
        }
      }

      // Normalize startDate: API may return full ISO timestamp, input[type=date] needs YYYY-MM-DD
      const normalizeDate = (d) => {
        if (!d) return new Date().toISOString().split('T')[0];
        return d.split('T')[0]; // strips time portion from ISO string if present
      };

      // Normalize timeOfDay: API returns HH:MM:SS, input[type=time] needs HH:MM
      const normalizeTime = (t) => {
        if (!t) return '18:30';
        return t.substring(0, 5); // "12:30:00" → "12:30"
      };

      setFormData({
        locationId: schedule.locationId || '',
        gameType: schedule.gameType || 'Texas Holdem',
        maxPlaces: schedule.maxPlaces || 10,
        numTables: schedule.numTables || 1,
        recurrenceType,
        isRecurring: schedule.isRecurring !== undefined ? schedule.isRecurring : true,
        dayOfWeek: schedule.dayOfWeek || [],
        timeOfDay: normalizeTime(schedule.timeOfDay),
        startDate: normalizeDate(schedule.startDate),
        endDate: schedule.endDate ? schedule.endDate.split('T')[0] : '',
        isActive: schedule.isActive !== undefined ? schedule.isActive : true,
      });

      // Populate existing dealer assignments
      const assignments = {};
      for (const d of (schedule.dealers || [])) {
        assignments[d.tableNumber] = d.dealerId;
      }
      setDealerAssignments(assignments);
    } else {
      setEditingSchedule(null);
      setFormData({
        locationId: venues[0]?.id || '',
        gameType: 'Texas Holdem',
        maxPlaces: 10,
        numTables: 1,
        recurrenceType: 'weekly',
        isRecurring: true,
        dayOfWeek: [],
        timeOfDay: '18:30',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        isActive: true,
      });
      setDealerAssignments({});
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSchedule(null);
    setDealerAssignments({});
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const numTables = parseInt(formData.numTables, 10) || 1;
      const scheduleData = {
        leagueId: user?.league?.id || leagueIdRef.current,
        locationId: formData.locationId,
        gameType: formData.gameType,
        maxPlaces: parseInt(formData.maxPlaces, 10) || 10,
        numTables,
        isRecurring: formData.isRecurring,
        dayOfWeek: formData.isRecurring ? formData.dayOfWeek : null,
        timeOfDay: formData.timeOfDay.length === 5 ? formData.timeOfDay + ':00' : formData.timeOfDay, // ensure HH:MM:SS
        startDate: formData.startDate,
        endDate: formData.endDate || null,
        isActive: formData.isActive,
      };

      let scheduleId;
      if (editingSchedule) {
        await schedulesApi.update(editingSchedule.id, scheduleData);
        scheduleId = editingSchedule.id;
        setSuccess('Schedule updated successfully!');
      } else {
        const result = await schedulesApi.create(scheduleData);
        scheduleId = result.data?.schedule?.id || result.schedule?.id;
        setSuccess('Schedule created successfully!');

        // Auto-generate games for new schedules
        if (scheduleId) {
          handleCloseModal();
          await handleGenerateGames(scheduleId);
          setSaving(false);
          return; // handleGenerateGames handles fetchData and success
        }
      }

      // Save dealer assignments
      if (scheduleId) {
        const existingDealers = editingSchedule?.dealers || [];
        const existingByTable = {};
        for (const d of existingDealers) existingByTable[d.tableNumber] = d.dealerId;

        for (let t = 1; t <= numTables; t++) {
          const newDealerId = dealerAssignments[t] || '';
          const oldDealerId = existingByTable[t] || '';

          if (newDealerId && newDealerId !== oldDealerId) {
            // Add or update dealer for this table
            await schedulesApi.setDealer(scheduleId, t, newDealerId);
          } else if (!newDealerId && oldDealerId) {
            // Remove dealer that was cleared
            await schedulesApi.removeDealer(scheduleId, t);
          }
        }

        // Remove dealers for tables that no longer exist (if numTables was reduced)
        for (const d of existingDealers) {
          if (d.tableNumber > numTables) {
            await schedulesApi.removeDealer(scheduleId, d.tableNumber);
          }
        }
      }

      handleCloseModal();
      fetchData();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving schedule:', err);
      setError(err.message || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Delete this schedule? Future games from this schedule will remain unless you delete them separately.')) {
      return;
    }

    try {
      await schedulesApi.delete(scheduleId, false);
      setSuccess('Schedule deleted successfully!');
      fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting schedule:', err);
      setError(err.message || 'Failed to delete schedule');
    }
  };

  const handleToggleActive = async (schedule) => {
    try {
      await schedulesApi.toggleActive(schedule.id, !schedule.isActive);
      setSuccess(`Schedule ${!schedule.isActive ? 'activated' : 'paused'}!`);
      fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error toggling schedule:', err);
      setError(err.message || 'Failed to update schedule');
    }
  };

  const handleGenerateGames = async (scheduleId) => {
    const schedule = schedules.find(s => s.id === scheduleId);
    const scheduleName = schedule?.locationName || 'Schedule';

    try {
      setGenerating(true);
      setGenerateProgress({ step: 'Preparing schedule...', percent: 10, scheduleName });

      await new Promise(r => setTimeout(r, 400));
      setGenerateProgress({ step: 'Calculating game dates...', percent: 30, scheduleName });

      await new Promise(r => setTimeout(r, 300));
      setGenerateProgress({ step: 'Creating games & tables...', percent: 50, scheduleName });

      const result = await schedulesApi.generateGames(scheduleId, 8);
      const count = result.data?.gamesCreated || result.gamesCreated || 0;

      setGenerateProgress({ step: 'Assigning dealers...', percent: 80, scheduleName });
      await new Promise(r => setTimeout(r, 300));

      setGenerateProgress({ step: `${count} games generated!`, percent: 100, scheduleName });
      await new Promise(r => setTimeout(r, 800));

      setSuccess(`Generated ${count} games for ${scheduleName}!`);
      fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error generating games:', err);
      setError(err.message || 'Failed to generate games');
    } finally {
      setGenerating(false);
      setGenerateProgress({ step: '', percent: 0, scheduleName: '' });
    }
  };

  const handleRecurrenceTypeChange = (type) => {
    const updates = { recurrenceType: type };

    if (type === 'once') {
      updates.isRecurring = false;
      updates.dayOfWeek = [];
      updates.endDate = '';
    } else if (type === 'daily') {
      updates.isRecurring = true;
      updates.dayOfWeek = [0, 1, 2, 3, 4, 5, 6]; // All days
    } else if (type === 'weekly') {
      updates.isRecurring = true;
      updates.dayOfWeek = []; // User will select specific day(s)
    } else if (type === 'custom') {
      updates.isRecurring = true;
      // Keep existing dayOfWeek selection
    }

    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleDayToggle = (dayValue) => {
    setFormData(prev => ({
      ...prev,
      dayOfWeek: prev.dayOfWeek.includes(dayValue)
        ? prev.dayOfWeek.filter(d => d !== dayValue)
        : [...prev.dayOfWeek, dayValue].sort((a, b) => a - b),
    }));
  };

  const handleDealerChange = (tableNumber, dealerId) => {
    setDealerAssignments(prev => ({
      ...prev,
      [tableNumber]: dealerId,
    }));
  };

  const formatDays = (dayOfWeek) => {
    if (!dayOfWeek || dayOfWeek.length === 0) return 'No days selected';
    if (dayOfWeek.length === 7) return 'Daily (Every day)';
    return dayOfWeek
      .sort((a, b) => a - b)
      .map(d => DAYS_OF_WEEK.find(day => day.value === d)?.short)
      .join(', ');
  };

  const formatRecurrence = (schedule) => {
    if (!schedule.isRecurring) return 'One-time';
    if (!schedule.dayOfWeek || schedule.dayOfWeek.length === 0) return 'Recurring (No days)';
    if (schedule.dayOfWeek.length === 7) return 'Daily';
    return `Weekly (${formatDays(schedule.dayOfWeek)})`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (timeOfDay) => {
    if (!timeOfDay) return '';
    const [hours, minutes] = timeOfDay.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Build numTables-length array for the form dealer section
  const tableNumbers = Array.from(
    { length: parseInt(formData.numTables, 10) || 1 },
    (_, i) => i + 1
  );

  return (
    <div className="page-container">
      <Sidebar user={user} onLogout={onLogout} />

      <main className="page-content">
        <div className="page-header">
          <div>
            <h1>Game Schedules</h1>
            <p>Manage recurring game schedules</p>
          </div>
          <button className="btn btn-primary" onClick={() => handleOpenModal()}>
            + New Schedule
          </button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading schedules...</p>
          </div>
        ) : schedules.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📅</div>
            <h3>No Schedules Yet</h3>
            <p>Create your first recurring game schedule</p>
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
              Create Schedule
            </button>
          </div>
        ) : (
          <div className="schedules-grid">
            {schedules.map(schedule => (
              <div key={schedule.id} className={`schedule-card ${!schedule.isActive ? 'inactive' : ''}`}>
                <div className="schedule-header">
                  <h3>{schedule.locationName || 'Unknown Venue'}</h3>
                  <span className={`badge ${schedule.isActive ? 'badge-success' : 'badge-secondary'}`}>
                    {schedule.isActive ? 'Active' : 'Paused'}
                  </span>
                </div>

                <div className="schedule-details">
                  <div className="detail-row">
                    <span className="label">🎴 Game:</span>
                    <span>{schedule.gameType}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">👥 Players:</span>
                    <span>{schedule.maxPlaces}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">🪑 Tables:</span>
                    <span>{schedule.numTables || 1}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">🔄 Recurrence:</span>
                    <span>{formatRecurrence(schedule)}</span>
                  </div>
                  {schedule.isRecurring ? (
                    <>
                      <div className="detail-row">
                        <span className="label">🕐 Time:</span>
                        <span>{formatTime(schedule.timeOfDay)}{schedule.leagueTimezone ? ` (${schedule.leagueTimezone.split('/').pop().replace('_', ' ')})` : ''}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">📅 Ends:</span>
                        <span>{schedule.endDate ? formatDate(schedule.endDate) : 'No end date'}</span>
                      </div>
                    </>
                  ) : (
                    <div className="detail-row">
                      <span className="label">📅 Date:</span>
                      <span>{formatDate(schedule.startDate)} at {formatTime(schedule.timeOfDay)}{schedule.leagueTimezone ? ` (${schedule.leagueTimezone.split('/').pop().replace('_', ' ')})` : ''}</span>
                    </div>
                  )}

                  {/* Default dealer assignments display */}
                  {schedule.dealers && schedule.dealers.length > 0 && (
                    <div className="dealer-assignments">
                      <div className="dealer-assignments-title">🃏 Default Dealers</div>
                      {schedule.dealers.map(d => (
                        <div key={d.tableNumber} className="detail-row dealer-row">
                          <span className="label">Table {d.tableNumber}:</span>
                          <span>{d.dealerName}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="schedule-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleOpenModal(schedule)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleToggleActive(schedule)}
                  >
                    {schedule.isActive ? 'Pause' : 'Activate'}
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleGenerateGames(schedule.id)}
                    disabled={!schedule.isActive}
                  >
                    Generate Games
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(schedule.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Generate Games Progress Overlay */}
        {generating && (
          <div className="generate-overlay">
            <div className="generate-modal">
              <div className="generate-icon">🎴</div>
              <h3>Generating Games</h3>
              <p className="generate-schedule-name">{generateProgress.scheduleName}</p>
              <div className="generate-progress-bar">
                <div
                  className="generate-progress-fill"
                  style={{ width: `${generateProgress.percent}%` }}
                />
              </div>
              <p className="generate-step">{generateProgress.step}</p>
              <p className="generate-percent">{generateProgress.percent}%</p>
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingSchedule ? 'Edit Schedule' : 'Create Schedule'}</h2>
                <button className="modal-close" onClick={handleCloseModal}>×</button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Venue * {venues.length === 0 && <small style={{color: '#e74c3c'}}>(No venues available)</small>}</label>
                  <select
                    className="form-input"
                    value={formData.locationId}
                    onChange={(e) => setFormData(prev => ({ ...prev, locationId: e.target.value }))}
                    required
                  >
                    <option value="">Select a venue</option>
                    {venues.map(venue => (
                      <option key={venue.id} value={venue.id}>{venue.name}</option>
                    ))}
                  </select>
                  {venues.length === 0 && (
                    <small style={{ display: 'block', marginTop: '4px', color: '#7f8c8d' }}>
                      Please create a venue first from the Venues page
                    </small>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Game Type *</label>
                    <select
                      className="form-input"
                      value={formData.gameType}
                      onChange={(e) => setFormData(prev => ({ ...prev, gameType: e.target.value }))}
                      required
                    >
                      <option value="Texas Holdem">Texas Hold'em</option>
                      <option value="Omaha">Omaha</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Max Players *</label>
                    <input
                      type="number"
                      className="form-input"
                      value={formData.maxPlaces}
                      onChange={(e) => setFormData(prev => ({ ...prev, maxPlaces: e.target.value }))}
                      min="2"
                      max="12"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Number of Tables *</label>
                    <input
                      type="number"
                      className="form-input"
                      value={formData.numTables}
                      onChange={(e) => setFormData(prev => ({ ...prev, numTables: e.target.value }))}
                      min="1"
                      max="20"
                      required
                    />
                  </div>
                </div>

                {/* Default Dealer Assignments — one row per table */}
                <div className="form-group">
                  <label>Default Dealers</label>
                  <div className="dealer-table-assignments">
                    {tableNumbers.map(tableNum => (
                      <div key={tableNum} className="dealer-table-row">
                        <span className="dealer-table-label">Table {tableNum}</span>
                        <select
                          className="form-input dealer-select"
                          value={dealerAssignments[tableNum] || ''}
                          onChange={(e) => handleDealerChange(tableNum, e.target.value)}
                        >
                          <option value="">— No default dealer —</option>
                          {dealers.map(d => (
                            <option key={d.id} value={d.id}>
                              {d.firstName || d.first_name} {d.lastName || d.last_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                  {dealers.length === 0 && (
                    <small style={{ color: '#7f8c8d', marginTop: '4px', display: 'block' }}>
                      No dealers found. Add users with the dealer role to assign them.
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Recurrence Pattern *</label>
                  <div className="recurrence-selector">
                    <button
                      type="button"
                      className={`recurrence-button ${formData.recurrenceType === 'once' ? 'selected' : ''}`}
                      onClick={() => handleRecurrenceTypeChange('once')}
                    >
                      One-time
                    </button>
                    <button
                      type="button"
                      className={`recurrence-button ${formData.recurrenceType === 'daily' ? 'selected' : ''}`}
                      onClick={() => handleRecurrenceTypeChange('daily')}
                    >
                      Daily
                    </button>
                    <button
                      type="button"
                      className={`recurrence-button ${formData.recurrenceType === 'weekly' ? 'selected' : ''}`}
                      onClick={() => handleRecurrenceTypeChange('weekly')}
                    >
                      Weekly
                    </button>
                    <button
                      type="button"
                      className={`recurrence-button ${formData.recurrenceType === 'custom' ? 'selected' : ''}`}
                      onClick={() => handleRecurrenceTypeChange('custom')}
                    >
                      Custom Days
                    </button>
                  </div>
                  <small style={{ display: 'block', marginTop: '8px', color: '#666' }}>
                    {formData.recurrenceType === 'once' && 'Game will occur only once'}
                    {formData.recurrenceType === 'daily' && 'Game will repeat every day'}
                    {formData.recurrenceType === 'weekly' && 'Select specific day(s) of the week below'}
                    {formData.recurrenceType === 'custom' && 'Select any combination of days below'}
                  </small>
                </div>

                {(formData.recurrenceType === 'weekly' || formData.recurrenceType === 'custom') && (
                  <div className="form-group">
                    <label>Days of Week *</label>
                    <div className="days-selector">
                      {DAYS_OF_WEEK.map(day => (
                        <button
                          key={day.value}
                          type="button"
                          className={`day-button ${formData.dayOfWeek.includes(day.value) ? 'selected' : ''}`}
                          onClick={() => handleDayToggle(day.value)}
                        >
                          {day.short}
                        </button>
                      ))}
                    </div>
                    {formData.dayOfWeek.length === 0 && (
                      <small style={{ color: '#e74c3c', marginTop: '4px', display: 'block' }}>
                        Please select at least one day
                      </small>
                    )}
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>Time *</label>
                    <input
                      type="time"
                      className="form-input"
                      value={formData.timeOfDay}
                      onChange={(e) => setFormData(prev => ({ ...prev, timeOfDay: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Start Date *</label>
                    <input
                      type="date"
                      className="form-input"
                      value={formData.startDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                {formData.isRecurring && (
                  <div className="form-group">
                    <label>End Date (Optional)</label>
                    <input
                      type="date"
                      className="form-input"
                      value={formData.endDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                      min={formData.startDate}
                    />
                    <small>Leave empty for no end date</small>
                  </div>
                )}

                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : editingSchedule ? 'Update Schedule' : 'Create Schedule'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Schedules;
